import React, { useEffect, useState } from "react";
import { Pause, Phone, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useEndCallParticipantMutation,
  useHoldCallParticipantMutation,
  useResumeCallParticipantMutation,
} from "@/store/rest_api";
import { CallParticipant } from "@/model/interface/Call";
import { apiErrorMessage } from "@/util/apiError";
import { formatTime } from "@/util/date";
import { getInitialsFromFullName } from "@/util/string";
import { memberName } from "@/util/member";

interface CallParticipantListProps {
  callId: string;
  participants: CallParticipant[];
  ownMembershipId?: string;
  leadName: string;
}

function participantName(participant: CallParticipant, leadName: string): string {
  if (participant.role === "REMOTE") {
    return leadName;
  }
  if (participant.role === "EXTERNAL") {
    return participant.number ?? "Phone number";
  }
  return memberName(participant.membership);
}

function secondsSince(timestamp: string | null | undefined, now: number): number {
  if (!timestamp) {
    return 0;
  }
  return Math.max(0, Math.floor((now - new Date(timestamp).getTime()) / 1000));
}

export default function CallParticipantList({
  callId,
  participants,
  ownMembershipId,
  leadName,
}: CallParticipantListProps) {
  const [holdParticipant] = useHoldCallParticipantMutation();
  const [resumeParticipant] = useResumeCallParticipantMutation();
  const [endParticipant] = useEndCallParticipantMutation();
  const [now, setNow] = useState(() => Date.now());

  const runAction = async (action: () => Promise<unknown>, failureMessage: string) => {
    try {
      await action();
    } catch (error) {
      toast.error(apiErrorMessage(error, failureMessage));
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="px-0.5 pb-0.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        Participants · {participants.length}
      </div>
      {participants.map((participant) => {
        const isYou = participant.role === "AGENT" && String(participant.membership?.id) === ownMembershipId;
        const name = isYou ? "You" : participantName(participant, leadName);
        const isHeld = Boolean(participant.heldAt);
        const isRinging = participant.status === "RINGING";
        const holdLabel = isHeld ? "Resume" : "Hold";
        return (
          <div
            key={participant.id}
            className={cn("flex items-center gap-2.5 rounded-lg border px-2.5 py-2", isYou && "bg-muted/40")}
          >
            <span
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                isYou ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
              )}
            >
              {participant.role === "EXTERNAL" ? <Phone className="h-3.5 w-3.5" /> : getInitialsFromFullName(name)}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-bold tabular-nums">{name}</span>
              {isYou && <span className="text-xs text-muted-foreground">Hang up to leave; the others stay on</span>}
              {!isYou && isRinging && <span className="text-xs font-semibold text-warning">Ringing</span>}
              {!isYou && !isRinging && isHeld && (
                <span className="status-pill-warning self-start tabular-nums">
                  <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                  On hold {formatTime(secondsSince(participant.heldAt, now))}
                </span>
              )}
              {!isYou && !isRinging && !isHeld && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold tabular-nums text-success">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" />
                  {participant.role === "REMOTE" ? "Lead · " : ""}Talking {formatTime(secondsSince(participant.joinedAt, now))}
                </span>
              )}
            </div>
            {!isYou && !isRinging && (
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full"
                title={holdLabel}
                aria-label={`${holdLabel} ${name}`}
                onClick={() =>
                  runAction(
                    () =>
                      (isHeld ? resumeParticipant : holdParticipant)({
                        callId,
                        participantId: participant.id,
                      }).unwrap(),
                    isHeld ? "Could not take them off hold" : "Could not put them on hold",
                  )
                }
              >
                {isHeld ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              </Button>
            )}
            {!isYou && (
              <Button
                size="icon"
                className="h-8 w-8 rounded-full bg-destructive-soft text-destructive-soft-foreground hover:bg-destructive-soft/80"
                title="End"
                aria-label={`End ${name}'s leg`}
                onClick={() =>
                  runAction(
                    () => endParticipant({ callId, participantId: participant.id }).unwrap(),
                    "Could not end their leg",
                  )
                }
              >
                <Phone className="h-3.5 w-3.5 rotate-135" />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
